import { GenericLazyString } from '@eventbrite/i18n';
import React from 'react';

export interface INotificationContextInitialState {
    generalNotification?: INotificationContextItem | {};
    localNotification?: INotificationContextItem | {};
}

export interface ICTAConfigProps {
    action?: () => void;
    link?: string;
    message?: GenericLazyString | string;
}

export interface INotificationContextItem {
    message: GenericLazyString | string;
    iconType?: JSX.Element;
    extraMessage?: GenericLazyString | string;
    cta?: ICTAConfigProps;
}

export interface INotificationContext {
    methods: {
        setGeneralNotification: (
            notificationItem: INotificationContextItem | {},
        ) => void;
        setLocalNotification: (
            notificationItem: INotificationContextItem | {},
        ) => void;
    };
    state: {
        generalNotification: INotificationContextItem | {};
        localNotification: INotificationContextItem | {};
    };
}

export const useNotificationsContext = () => {
    const value = React.useContext(NotificationsContext);

    if (!value) {
        throw new Error(
            'useNotificationsContext must be used within FrontendContext',
        );
    }

    return value;
};

const NotificationsContext = React.createContext<any>(null);

const NotificationsContextProvider = ({
    children,
    initialState,
}: {
    children: React.ReactNode;
    initialState?: Partial<INotificationContextInitialState>;
}) => {
    const [generalNotification, setGeneralNotification] = React.useState<
        INotificationContextItem | {}
    >(initialState?.generalNotification ?? {});
    const [localNotification, setLocalNotification] = React.useState<
        INotificationContextItem | {}
    >(initialState?.localNotification ?? {});

    const exportedValues: INotificationContext = {
        methods: {
            setGeneralNotification,
            setLocalNotification,
        },
        state: {
            generalNotification,
            localNotification,
        },
    };

    return (
        <NotificationsContext.Provider value={exportedValues}>
            {children}
        </NotificationsContext.Provider>
    );
};

export default {
    Context: NotificationsContext,
    Consumer: NotificationsContext.Consumer,
    Provider: NotificationsContextProvider,
};
